import { apiRequest } from '@nusrif/utils';
import {
  Product,
  ProductListQuery,
  ProductListResponse,
  ProductDetailResponse,
  SortOptions,
  FilterOptions,
  ProductFilters,
  ProductSort,
  ProductVariant,
  Asset,
  ProductOptionGroup,
  FacetValue
} from '@nusrif/types';

// In-memory cache for product details
const productByIdCache = new Map<string, ProductDetailResponse>();
// In-memory cache for product list queries
const productsCache = new Map<string, ProductListResponse>();

/**
 * Get a paginated list of products with optional filtering and sorting
 */
export async function getProducts(query?: ProductListQuery): Promise<ProductListResponse> {
  const cacheKey = query ? JSON.stringify(query) : '__all__';
  if (productsCache.has(cacheKey)) {
    // Fire-and-forget: update cache in the background
    apiRequest<ProductListResponse>('/products', {
      method: 'GET',
      params: query as Record<string, string | number | boolean>,
    })
      .then(response => {
        if (response.success) {
          productsCache.set(cacheKey, response);
        }
      })
      .catch(() => {});
    // Return cached data immediately
    return productsCache.get(cacheKey)!;
  }
  const response = await apiRequest<ProductListResponse>('/products', {
    method: 'GET',
    params: query as Record<string, string | number | boolean>,
  });
  if (response.success) {
    productsCache.set(cacheKey, response);
  }
  return response;
}

/**
 * Get detailed information about a specific product by ID
 */
export async function getProductById(id: string): Promise<ProductDetailResponse> {
  // First, check if the product exists in any cached product list
  for (const cachedResponse of productsCache.values()) {
    if (cachedResponse.success && Array.isArray(cachedResponse.data.items)) {
      const product = cachedResponse.data.items.find((p: any) => p.id === id);
      if (product) {
        // Fire-and-forget: update cache in the background
        apiRequest<ProductDetailResponse>(`/products/${id}`, { method: 'GET' })
          .then(response => {
            if (response.success) {
              productByIdCache.set(id, response);
            }
          })
          .catch(() => {});
        // Return cached data immediately
        return {
          success: true,
          data: product,
          message: 'Fetched from productsCache',
        };
      }
    }
  }

  // Then, check the productByIdCache
  if (productByIdCache.has(id)) {
    // Fire-and-forget: update cache in the background
    apiRequest<ProductDetailResponse>(`/products/${id}`, { method: 'GET' })
      .then(response => {
        if (response.success) {
          productByIdCache.set(id, response);
        }
      })
      .catch(() => {});
    // Return cached data immediately
    return productByIdCache.get(id)!;
  }

  // No cache, fetch from API and cache
  const response = await apiRequest<ProductDetailResponse>(`/products/${id}`, {
    method: 'GET',
  });
  if (response.success) {
    productByIdCache.set(id, response);
  }
  return response;
}

/**
 * Get a product by its slug
 */
export async function getProductBySlug(slug: string): Promise<ProductDetailResponse> {
  return apiRequest<ProductDetailResponse>(`/products/slug/${slug}`, {
    method: 'GET',
  });
}

/**
 * Get products with simplified filtering and sorting
 */
export async function getProductsWithFilters(
  options: {
    skip?: number;
    take?: number;
    filters?: ProductFilters;
    sort?: ProductSort;
  } = {}
): Promise<ProductListResponse> {
  const { skip = 0, take = 20, filters, sort } = options;
  
  const query: ProductListQuery = {
    skip,
    take: Math.min(take, 100), // Ensure max 100 items
  };

  // Add filters if provided
  if (filters && Object.keys(filters).length > 0) {
    query.filter = JSON.stringify(filters);
  }

  // Add sorting if provided
  if (sort && Object.keys(sort).length > 0) {
    query.sort = JSON.stringify(sort);
  }

  return getProducts(query);
}

/**
 * Get featured products (enabled products)
 */
export async function getFeaturedProducts(limit: number = 10): Promise<ProductListResponse> {
  return getProductsWithFilters({
    take: limit,
    filters: {
      enabled: { eq: true }
    },
    sort: {
      createdAt: 'DESC'
    }
  });
}

/**
 * Get products by category
 */
export async function getProductsByCategory(
  categoryCode: string,
  options: {
    skip?: number;
    take?: number;
    sort?: ProductSort;
  } = {}
): Promise<ProductListResponse> {
  return getProductsWithFilters({
    ...options,
    filters: {
      enabled: { eq: true },
      'facetValues.code': { eq: categoryCode }
    }
  });
}

/**
 * Search products by name
 */
export async function searchProducts(
  searchTerm: string,
  options: {
    skip?: number;
    take?: number;
    sort?: ProductSort;
  } = {}
): Promise<ProductListResponse> {
  return getProductsWithFilters({
    ...options,
    filters: {
      enabled: { eq: true },
      name: { contains: searchTerm }
    }
  });
}

/**
 * Get products within a price range
 */
export async function getProductsByPriceRange(
  minPrice: number,
  maxPrice: number,
  options: {
    skip?: number;
    take?: number;
    sort?: ProductSort;
  } = {}
): Promise<ProductListResponse> {
  return getProductsWithFilters({
    ...options,
    filters: {
      enabled: { eq: true },
      'variants.price': { gte: minPrice, lte: maxPrice }
    }
  });
}

/**
 * Get in-stock products
 */
export async function getInStockProducts(
  options: {
    skip?: number;
    take?: number;
    sort?: ProductSort;
  } = {}
): Promise<ProductListResponse> {
  return getProductsWithFilters({
    ...options,
    filters: {
      enabled: { eq: true },
      'variants.stockLevels.stockOnHand': { gt: 0 }
    }
  });
}

/**
 * Get products with advanced filtering
 */
export async function getProductsWithAdvancedFilters(
  filters: FilterOptions,
  options: {
    skip?: number;
    take?: number;
    sort?: SortOptions;
  } = {}
): Promise<ProductListResponse> {
  const { skip = 0, take = 20, sort } = options;
  
  const query: ProductListQuery = {
    skip,
    take: Math.min(take, 100),
    filter: JSON.stringify(filters),
  };

  if (sort && Object.keys(sort).length > 0) {
    query.sort = JSON.stringify(sort);
  }

  return getProducts(query);
}

/**
 * Get multiple products by IDs
 */
export async function getProductsByIds(ids: string[]): Promise<Product[]> {
  const promises = ids.map(id => getProductById(id));
  const responses = await Promise.allSettled(promises);
  
  const products: Product[] = [];
  responses.forEach((result) => {
    if (result.status === 'fulfilled' && result.value.success) {
      products.push(result.value.data);
    }
  });
  
  return products;
}

/**
 * Get product variants for a specific product
 */
export async function getProductVariants(productId: string): Promise<ProductVariant[]> {
  const response = await getProductById(productId);
  if (response.success) {
    return response.data.variants;
  }
  throw new Error(response.message || 'Failed to get product variants');
}

/**
 * Get product assets for a specific product
 */
export async function getProductAssets(productId: string): Promise<Asset[]> {
  const response = await getProductById(productId);
  if (response.success) {
    return response.data.assets;
  }
  throw new Error(response.message || 'Failed to get product assets');
}

/**
 * Get product option groups for a specific product
 */
export async function getProductOptionGroups(productId: string): Promise<ProductOptionGroup[]> {
  const response = await getProductById(productId);
  if (response.success) {
    return response.data.optionGroups;
  }
  throw new Error(response.message || 'Failed to get product option groups');
}

/**
 * Check if a product is in stock
 */
export function isProductInStock(product: Product): boolean {
  return product.variants.some(variant => {
    const totalStock = variant.stockLevels.reduce((sum, level) => 
      sum + level.stockOnHand - level.stockAllocated, 0
    );
    return totalStock > variant.outOfStockThreshold;
  });
}

/**
 * Get the lowest price variant for a product
 */
export function getLowestPriceVariant(product: Product): ProductVariant | null {
  if (product.variants.length === 0) return null;
  
  return product.variants.reduce((lowest, current) => 
    current.price < lowest.price ? current : lowest
  );
}

/**
 * Get the highest price variant for a product
 */
export function getHighestPriceVariant(product: Product): ProductVariant | null {
  if (product.variants.length === 0) return null;
  
  return product.variants.reduce((highest, current) => 
    current.price > highest.price ? current : highest
  );
}

/**
 * Get product price range
 */
export function getProductPriceRange(product: Product): { min: number; max: number } | null {
  if (product.variants.length === 0) return null;
  
  const prices = product.variants.map(variant => variant.price);
  return {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
}

/**
 * Get available options for a product
 */
export function getProductAvailableOptions(product: Product): ProductOptionGroup[] {
  return product.optionGroups.filter(group => group.options.length > 0);
}

/**
 * Get product categories from facet values
 */
export function getProductCategories(product: Product): FacetValue[] {
  return product.facetValues.filter(facet => 
    facet.facet.name.toLowerCase() === 'category'
  );
}

/**
 * Get new arrivals products (recently created products)
 */
export async function getNewArrivals(limit: number = 10): Promise<ProductListResponse> {
  return getProductsWithFilters({
    take: limit,
    filters: {
      enabled: { eq: true }
    },
    sort: {
      createdAt: 'DESC'
    }
  });
} 

/**
 * Check if a specific variant is in stock
 */
export function isVariantInStock(variant: ProductVariant): boolean {
  const totalStock = variant.stockLevels.reduce((sum, level) => 
    sum + level.stockOnHand - level.stockAllocated, 0
  );
  return totalStock > variant.outOfStockThreshold;
}

/**
 * Get stock level for a specific variant
 */
export function getVariantStockLevel(variant: ProductVariant): number {
  return variant.stockLevels.reduce((sum, level) => 
    sum + level.stockOnHand - level.stockAllocated, 0
  );
}

/**
 * Find a variant by its options (size, color, etc.)
 */
export function findVariantByOptions(product: Product, options: { [key: string]: string }): ProductVariant | null {
  return product.variants.find(variant => {
    return Object.entries(options).every(([optionName, optionValue]) => {
      return variant.options.some(option => 
        option.name.toLowerCase() === optionValue.toLowerCase() ||
        option.code?.toLowerCase() === optionName.toLowerCase()
      );
    });
  }) || null;
}

/**
 * Get available variants for a product (in stock variants)
 */
export function getAvailableVariants(product: Product): ProductVariant[] {
  return product.variants.filter(variant => isVariantInStock(variant));
}

/**
 * Get stock status for all variants of a product
 */
export function getProductVariantStockStatus(product: Product): Array<{
  variant: ProductVariant;
  isInStock: boolean;
  stockLevel: number;
  stockStatus: 'in_stock' | 'low_stock' | 'out_of_stock';
}> {
  return product.variants.map(variant => {
    const stockLevel = getVariantStockLevel(variant);
    const isInStock = stockLevel > variant.outOfStockThreshold;
    
    let stockStatus: 'in_stock' | 'low_stock' | 'out_of_stock';
    if (stockLevel <= variant.outOfStockThreshold) {
      stockStatus = 'out_of_stock';
    } else if (stockLevel <= 5) { // Consider low stock if 5 or fewer items
      stockStatus = 'low_stock';
    } else {
      stockStatus = 'in_stock';
    }
    
    return {
      variant,
      isInStock,
      stockLevel,
      stockStatus
    };
  });
}

/**
 * Get stock status for specific options (e.g., size/color combinations)
 */
export function getOptionStockStatus(product: Product): {
  [optionKey: string]: {
    isInStock: boolean;
    stockLevel: number;
    stockStatus: 'in_stock' | 'low_stock' | 'out_of_stock';
    variantId: string;
  };
} {
  const stockStatus: { [key: string]: any } = {};
  
  product.variants.forEach(variant => {
    const optionKey = variant.options.map(opt => opt.name).sort().join('_');
    const stockLevel = getVariantStockLevel(variant);
    const isInStock = stockLevel > variant.outOfStockThreshold;
    
    let stockStatusType: 'in_stock' | 'low_stock' | 'out_of_stock';
    if (stockLevel <= variant.outOfStockThreshold) {
      stockStatusType = 'out_of_stock';
    } else if (stockLevel <= 5) {
      stockStatusType = 'low_stock';
    } else {
      stockStatusType = 'in_stock';
    }
    
    stockStatus[optionKey] = {
      isInStock,
      stockLevel,
      stockStatus: stockStatusType,
      variantId: variant.id
    };
  });
  
  return stockStatus;
} 